import { CloudUpload, RefreshCw, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/button";
import { Dialog } from "@/components/dialog";
import { Modal } from "@/components/modal";

import {
  acceptAttributeForMime,
  formatLabelForMime,
  isSameImageFormat,
} from "../utils/sessionFileNaming";

interface ReplaceFileModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onReplace: (file: File) => void;
  originalMime: string;
}

/**
 * Single-file picker used from `ResultsStep` to swap a previously-uploaded
 * design for a corrected version. The file is forwarded to the parent's
 * `onReplace`, which uploads via my-files multipart and calls the replace endpoint.
 */
export function ReplaceFileModal({
  isOpen,
  onOpenChange,
  onReplace,
  originalMime,
}: ReplaceFileModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<File | null>(null);
  const [formatError, setFormatError] = useState<null | string>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelected(null);
      setFormatError(null);
    }
  }, [isOpen]);

  const handleFiles = (files: File[]) => {
    const first = files[0];

    if (!first) return;

    if (!isSameImageFormat(first, originalMime)) {
      setSelected(null);
      setFormatError(
        `Please upload a ${formatLabelForMime(originalMime)} file to match the original.`,
      );

      return;
    }

    setFormatError(null);
    setSelected(first);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles([...(event.target.files ?? [])]);
    event.target.value = "";
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    handleFiles([...event.dataTransfer.files]);
  };

  const handleConfirm = () => {
    if (!selected) return;

    onReplace(selected);
    onOpenChange(false);
  };

  const accept = acceptAttributeForMime(originalMime);
  const formatLabel = formatLabelForMime(originalMime);

  return (
    <Modal
      classNames={{ modal: "flex w-[480px] flex-col overflow-hidden" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog className="flex flex-col">
        <div className="flex items-start justify-between border-b border-border-secondary px-6 py-4">
          <span className="text-base font-semibold text-text-primary">
            Replace file
          </span>
          <button
            aria-label="Close"
            className="rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-bg-secondary"
            onClick={() => onOpenChange(false)}
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          <p className="text-sm text-text-tertiary">
            Upload a corrected version of this file. We will reanalyze it
            against all criteria. If it scores 100%, it will be automatically
            marked as checked.
          </p>

          <div
            className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border border-border-secondary bg-bg-primary p-6 text-center transition-colors hover:bg-bg-primary-hover"
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border-secondary bg-bg-primary shadow-sm">
              <CloudUpload className="text-text-secondary" size={20} />
            </div>
            <div>
              <p className="text-sm">
                <span className="font-medium text-text-brand-primary">
                  Click to upload
                </span>{" "}
                <span className="text-text-tertiary">or drag and drop</span>
              </p>
              <p className="mt-1 text-xs text-text-tertiary">
                {formatLabel} only
              </p>
            </div>
            <input
              accept={accept}
              className="hidden"
              onChange={handleFileChange}
              ref={inputRef}
              type="file"
            />
          </div>

          {formatError && (
            <p className="text-sm text-text-error-primary">{formatError}</p>
          )}

          {selected && (
            <div className="rounded-md border border-border-secondary bg-bg-primary px-3 py-2 text-sm text-text-primary">
              <span className="truncate">{selected.name}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border-secondary px-6 py-4">
          <Button
            color="gray"
            onPress={() => onOpenChange(false)}
            size="lg"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            color="color"
            isDisabled={!selected}
            onPress={handleConfirm}
            size="lg"
            variant="primary"
          >
            <RefreshCw size={14} />
            Replace file
          </Button>
        </div>
      </Dialog>
    </Modal>
  );
}
